import type { CopilotState } from '../../pages/copilot/stores/useCopilotStore';

/**
 * The chat itself: its messages, the timeline they sit in, the turn in
 * progress, and what a turn can leave behind — results, approvals, downloads,
 * findings, audits.
 *
 * Written once for every chat. The Copilot page's store is this plus its own
 * page-only state; each docked chat gets a store of just this
 * (`createChatStore`). So a fix here reaches every place a chat appears.
 *
 * The types are still declared on the Copilot store; they move here when the
 * panel is separated from that store.
 */
type ChatSliceKey =
  | 'threadId' | 'setThreadId' | 'threadTitle' | 'setThreadTitle'
  | 'isLoadingHistory' | 'setIsLoadingHistory'
  | 'timeline' | 'appendTimelineUser' | 'appendTimelineArtifact' | 'ensureTimelineStreamingAssistant'
  | 'finalizeTimelineAssistant' | 'popTimelineStreamingIfStuck' | 'appendTimelineSqlApproval'
  | 'appendTimelineVoiceCallApproval' | 'appendTimelineVoiceCall' | 'appendTimelineDownload'
  | 'appendTimelineAssistantMessage' | 'appendTimelineWorkbook' | 'clearTimeline'
  | 'messages' | 'addMessage' | 'clearMessages'
  | 'auditStubs' | 'auditReports' | 'addAuditReport' | 'pendingAuditId' | 'setPendingAuditId'
  | 'isStreaming' | 'setIsStreaming' | 'streamingContent' | 'setStreamingContent' | 'appendStreamingContent'
  | 'streamingStatuses' | 'appendStreamingStatus' | 'appendStreamingToolStep' | 'attachStreamingReceipt'
  | 'setStreamingStatuses' | 'streamingAction' | 'setStreamingAction' | 'streamingToolCallCount'
  | 'noteStreamingToolCall' | 'streamingStartedAt' | 'streamingLastEventAt' | 'touchStreamingActivity'
  | 'pendingSQLApproval' | 'setPendingSQLApproval'
  | 'pendingVoiceCallApproval' | 'setPendingVoiceCallApproval' | 'voiceCallModalOpen' | 'setVoiceCallModalOpen'
  | 'voiceCallModalCallId' | 'openVoiceCall' | 'voiceCallSummaries' | 'addVoiceCallSummary'
  | 'downloadOffers' | 'addDownloadOffer'
  | 'workbooks' | 'addWorkbook'
  | 'workspaceTabs' | 'activeTabId' | 'addWorkspaceTab' | 'removeWorkspaceTab' | 'setActiveTab'
  | 'patchWorkspaceArtifact' | 'seenArtifactIds' | 'markArtifactSeen'
  | 'schemaTree' | 'setSchemaTree'
  | 'findings' | 'addFinding'
  | 'contextUsage' | 'setContextUsage'
  | 'attachmentCache' | 'upsertAttachmentCache' | 'clearAttachmentCache'
  | 'inputText' | 'setInputText';

export type ChatSlice = Pick<CopilotState, ChatSliceKey> & {
  /** Clear the turn in progress: its text, its action line and its steps. */
  resetStreaming: () => void;
};

export type ChatSet = (
  partial: Partial<ChatSlice> | ((state: ChatSlice) => Partial<ChatSlice>),
) => void;

type AuditReport = ChatSlice['auditReports'][string];

export function createChatSlice(set: ChatSet): ChatSlice {
  return {
    // Thread
    threadId: null,
    setThreadId: (id) => set({ threadId: id }),
    threadTitle: null,
    setThreadTitle: (title) => set({ threadTitle: title }),

    isLoadingHistory: false,
    setIsLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

    // Timeline — the order things appear in: user → … → artifacts → assistant → approvals
    timeline: [],
    appendTimelineUser: (messageId) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'user', messageId }] })),
    appendTimelineArtifact: (artifactId) =>
      set((s) => {
        if (s.timeline.some((e) => e.type === 'artifact' && e.artifactId === artifactId)) return {};
        return { timeline: [...s.timeline, { type: 'artifact', artifactId }] };
      }),
    ensureTimelineStreamingAssistant: () =>
      set((s) => {
        const last = s.timeline[s.timeline.length - 1];
        if (last?.type === 'streaming_assistant') return {};
        // There can only ever be one live slot: it renders the entire streaming
        // step list, so a second one duplicates the whole feed on screen. When
        // an artifact has landed since the last slot was opened, move the slot
        // to the end rather than adding another.
        const withoutStreaming = s.timeline.filter((e) => e.type !== 'streaming_assistant');
        return { timeline: [...withoutStreaming, { type: 'streaming_assistant' }] };
      }),
    finalizeTimelineAssistant: (messageId) =>
      set((s) => {
        const t = [...s.timeline];
        const last = t[t.length - 1];
        if (last?.type === 'streaming_assistant') {
          t[t.length - 1] = { type: 'assistant', messageId };
        } else {
          t.push({ type: 'assistant', messageId });
        }
        return { timeline: t };
      }),
    popTimelineStreamingIfStuck: () =>
      set((s) => {
        const last = s.timeline[s.timeline.length - 1];
        if (last?.type === 'streaming_assistant') {
          return { timeline: s.timeline.slice(0, -1) };
        }
        return {};
      }),
    appendTimelineSqlApproval: (id) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'sql_approval', id }] })),
    appendTimelineVoiceCallApproval: (id) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'voice_call_approval', id }] })),
    appendTimelineVoiceCall: (callId) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'voice_call', id: callId }] })),
    appendTimelineDownload: (offerId) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'download', id: offerId }] })),
    appendTimelineWorkbook: (workbookId) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'workbook', id: workbookId }] })),
    appendTimelineAssistantMessage: (messageId) =>
      set((s) => ({ timeline: [...s.timeline, { type: 'assistant', messageId }] })),
    clearTimeline: () => set({ timeline: [] }),

    // Messages
    messages: [],
    addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
    clearMessages: () => set({ messages: [] }),

    // Explainability audit (backend-native only)
    auditStubs: {},
    auditReports: {},
    addAuditReport: (report, reportId) => {
      const id = reportId || report.id || `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const normalized: AuditReport = {
        ...report,
        id,
      };
      set((prev) => ({
        auditReports: {
          ...prev.auditReports,
          [id]: normalized,
        },
      }));
      return id;
    },
    pendingAuditId: null,
    setPendingAuditId: (auditId) => set({ pendingAuditId: auditId }),

    // Streaming
    isStreaming: false,
    setIsStreaming: (streaming) =>
      set(() => ({
        isStreaming: streaming,
        ...(streaming
          ? {
              streamingStartedAt: Date.now(),
              streamingLastEventAt: Date.now(),
              streamingToolCallCount: 0,
            }
          : {
              streamingAction: '',
              streamingStartedAt: null,
              streamingLastEventAt: null,
              streamingToolCallCount: 0,
            }),
      })),
    streamingContent: '',
    setStreamingContent: (content) => set({ streamingContent: content }),
    appendStreamingContent: (chunk) =>
      set((s) => {
        if (!s.streamingContent) return { streamingContent: chunk };
        // Each agent_message event is a discrete block (a mid-run status sentence
        // or the final answer). Separate them with a blank line so they render on
        // their own lines instead of gluing together ("...chart.Now I will...").
        const sep = /\n\s*$/.test(s.streamingContent) ? '' : '\n\n';
        return { streamingContent: s.streamingContent + sep + chunk };
      }),
    streamingStatuses: [],
    appendStreamingStatus: (chunk) =>
      set((s) =>
        chunk
          ? {
              streamingStatuses: [
                ...s.streamingStatuses,
                { text: chunk, source: 'narration' as const },
              ],
            }
          : {},
      ),
    appendStreamingToolStep: (label) =>
      set((s) => {
        if (!label) return {};
        const steps = s.streamingStatuses;
        const last = steps[steps.length - 1];
        // The model already narrated this step; its sentence is more useful than
        // a generic tool label, so mark it covered rather than duplicating it.
        if (last && last.source === 'narration' && !last.toolCovered) {
          return { streamingStatuses: [...steps.slice(0, -1), { ...last, toolCovered: true }] };
        }
        // Collapse consecutive identical tool labels (retries, paged reads) so a
        // loop doesn't spam the feed with the same row.
        if (last && last.source === 'tool' && last.text === label && !last.receipt) {
          return {};
        }
        return { streamingStatuses: [...steps, { text: label, source: 'tool' as const }] };
      }),
    attachStreamingReceipt: (receipt) =>
      set((s) => {
        if (!receipt) return {};
        const steps = s.streamingStatuses;
        // Result arrived with no preceding step at all (model ran a tool silently
        // and we somehow missed the tool_call) — the receipt still earns a row.
        if (steps.length === 0) {
          return { streamingStatuses: [{ text: 'Fetched data', source: 'tool' as const, receipt }] };
        }
        const last = steps[steps.length - 1];
        return { streamingStatuses: [...steps.slice(0, -1), { ...last, receipt }] };
      }),
    setStreamingStatuses: (statuses) => set({ streamingStatuses: statuses }),
    streamingAction: '',
    setStreamingAction: (action: string) => set({ streamingAction: action }),
    streamingToolCallCount: 0,
    noteStreamingToolCall: () =>
      set((s) => ({ streamingToolCallCount: s.streamingToolCallCount + 1 })),
    streamingStartedAt: null,
    streamingLastEventAt: null,
    touchStreamingActivity: () => set({ streamingLastEventAt: Date.now() }),
    resetStreaming: () =>
      set({
        streamingContent: '',
        streamingAction: '',
        streamingStatuses: [],
      }),

    // SQL Approval
    pendingSQLApproval: null,
    setPendingSQLApproval: (approval) => set({ pendingSQLApproval: approval }),

    pendingVoiceCallApproval: null,
    setPendingVoiceCallApproval: (approval) => set({ pendingVoiceCallApproval: approval }),
    voiceCallModalOpen: false,
    setVoiceCallModalOpen: (open) =>
      // Closing clears the pinned call so the next live call follows the
      // thread again rather than reopening the one we just looked at.
      set(open ? { voiceCallModalOpen: true } : { voiceCallModalOpen: false, voiceCallModalCallId: null }),
    voiceCallModalCallId: null,
    openVoiceCall: (callId) => set({ voiceCallModalOpen: true, voiceCallModalCallId: callId }),
    voiceCallSummaries: {},
    addVoiceCallSummary: (summary) =>
      set((s) => ({
        voiceCallSummaries: { ...s.voiceCallSummaries, [summary.callId]: summary },
      })),

    downloadOffers: {},
    addDownloadOffer: (offer) =>
      set((s) => ({
        downloadOffers: { ...s.downloadOffers, [offer.id]: offer },
      })),

    // Workbooks the agent built. Stored server-side, so unlike a download
    // offer these stay good after the session's tables are gone.
    workbooks: {},
    addWorkbook: (workbook) =>
      set((s) => ({ workbooks: { ...s.workbooks, [workbook.id]: workbook } })),

    // Workspace artifacts
    workspaceTabs: [],
    activeTabId: null,
    addWorkspaceTab: (tab) =>
      set((s) => {
        const exists = s.workspaceTabs.some((t) => t.id === tab.id);
        return {
          workspaceTabs: exists
            ? s.workspaceTabs.map((t) => t.id === tab.id ? tab : t)
            : [...s.workspaceTabs, tab],
          activeTabId: tab.id,
          // Don't mark new artifacts as seen - they'll get the entrance animation + unread dot
        };
      }),
    removeWorkspaceTab: (tabId) =>
      set((s) => {
        const filtered = s.workspaceTabs.filter((t) => t.id !== tabId);
        return {
          workspaceTabs: filtered,
          activeTabId:
            s.activeTabId === tabId
              ? filtered.length > 0
                ? filtered[filtered.length - 1].id
                : null
              : s.activeTabId,
        };
      }),
    setActiveTab: (tabId) =>
      set((s) => {
        const next: Partial<ChatSlice> = { activeTabId: tabId };
        // Mark the artifact as seen when user selects it
        if (tabId && !s.seenArtifactIds.has(tabId)) {
          const updated = new Set(s.seenArtifactIds);
          updated.add(tabId);
          next.seenArtifactIds = updated;
        }
        return next;
      }),
    patchWorkspaceArtifact: (artifactId, patch) =>
      set((s) => ({
        workspaceTabs: s.workspaceTabs.map((t) =>
          t.id === artifactId ? ({ ...t, ...patch } as typeof t) : t,
        ),
      })),

    // Artifact visibility tracking
    seenArtifactIds: new Set<string>(),
    markArtifactSeen: (id) =>
      set((s) => {
        if (s.seenArtifactIds.has(id)) return {};
        const updated = new Set(s.seenArtifactIds);
        updated.add(id);
        return { seenArtifactIds: updated };
      }),

    // Schema tree
    schemaTree: [],
    setSchemaTree: (tree) => set({ schemaTree: tree }),

    // Pinned findings
    findings: [],
    addFinding: (finding) => set((s) => ({ findings: [...s.findings, finding] })),

    // Context usage
    contextUsage: null,
    setContextUsage: (usage) => set({ contextUsage: usage }),

    // Chat attachments cache
    attachmentCache: {},
    upsertAttachmentCache: (rows) =>
      set((prev) => {
        if (!rows.length) return prev;
        const next = { ...prev.attachmentCache };
        for (const r of rows) next[r.attachment_id] = r;
        return { attachmentCache: next };
      }),
    clearAttachmentCache: () => set({ attachmentCache: {} }),

    // Input
    inputText: '',
    setInputText: (text) => set({ inputText: text }),
  };
}
